import React, { useState, useEffect } from 'react'
import { Card, Form, DatePicker, Button, message, Row, Col, Space, Table, Input, Select, Divider, Tag, Tooltip, Modal } from 'antd'
import { SearchOutlined, ReloadOutlined, FileTextOutlined, DownloadOutlined, EyeOutlined } from '@ant-design/icons'
import moment from 'moment'
import * as XLSX from 'xlsx'
import JournalChannelFileService from '../../services/JournalChannelFileService'
import { hasActionByModule } from 'utilities/modules'
import Cookies from 'js-cookie'
import '../../static/less/app.less'

const { RangePicker } = DatePicker
const { Option } = Select

const JournalChannelFile = () => {
    
    
    const nbRowPreference = Cookies.get('nbRow') !== undefined ? Cookies.get('nbRow') : 10
    const [form] = Form.useForm()
    const [loading, setLoading] = useState(false)
    const [data, setData] = useState([])
    const [rawData, setRawData] = useState([])
    const [dateRange, setDateRange] = useState([
        moment().subtract(30, 'days'),
        moment()
    ])
    const [nbRow, setNbRow] = useState(nbRowPreference)
    const [searchText, setSearchText] = useState('')
    const [searchedColumn, setSearchedColumn] = useState('')
    const [previewVisible, setPreviewVisible] = useState(false)
    const [previewContent, setPreviewContent] = useState('')
    const [previewFilename, setPreviewFilename] = useState('')
    const [previewFileType, setPreviewFileType] = useState('text') // 'csv', 'excel' ou 'text'
    const [previewData, setPreviewData] = useState([]) // Pour stocker les données Excel parsées
    const [previewHeader, setPreviewHeader] = useState([]) // Pour stocker le bandeau/template (OUT)
    const [previewDirection, setPreviewDirection] = useState('IN') // Direction du fichier
    const [downloadLoading, setDownloadLoading] = useState(null)

    document.title = 'Historique'

    // Charger les données au montage du composant
    useEffect(() => {
        handleSearch()
    }, [])

    /**
     * Rechercher les fichiers du journal channel
     */
    const handleSearch = () => {
        if (!dateRange || dateRange.length !== 2) {
            message.warning('Veuillez sélectionner une période')
            return
        }

        setLoading(true)
        message.loading('Chargement des données en cours...', 0.5)
       const startDate = dateRange[0].startOf('day').format('YYYY-MM-DD HH:mm:ss')
        const endDate   = dateRange[1].endOf('day').format('YYYY-MM-DD HH:mm:ss')
        console.log('🔍 Recherche des fichiers du journal channel du', startDate, 'au', endDate)
        JournalChannelFileService.getJournalChannelFiles(startDate, endDate)
            .then((res) => {
                setData(res.data)
                setRawData(res.data)
                message.success(`${res.data.length} enregistrement(s) trouvé(s)`)
            })
            .catch((err) => {
                console.error('Erreur lors de la récupération des données:', err)
                message.error('Une erreur est survenue lors du chargement des données')
            })
            .finally(() => {
                setLoading(false)
            })
    }

    /**
     * Gestion de la recherche dans les colonnes
     */
    const handleColumnSearch = (selectedKeys, confirm, dataIndex) => {
        confirm()
        setSearchText(selectedKeys[0])
        setSearchedColumn(dataIndex)
        setData(rawData.filter(item =>
            item[dataIndex]
                ?.toString()
                .toLowerCase()
                .includes(selectedKeys[0].toLowerCase())
        ))
    }

    /**
     * Réinitialiser la recherche dans une colonne
     */
    const handleColumnReset = (clearFilters) => {
        clearFilters()
        setSearchText('')
        setData(rawData)
    }

    /**
     * Props pour la recherche dans les colonnes
     */
    const getColumnSearchProps = (dataIndex) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <div style={{ padding: 8 }}>
                <Input
                    placeholder={`Rechercher ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleColumnSearch(selectedKeys, confirm, dataIndex)}
                    style={{ width: 188, marginBottom: 8, display: 'block' }}
                />
                <Button
                    type="primary"
                    onClick={() => handleColumnSearch(selectedKeys, confirm, dataIndex)}
                    icon={<SearchOutlined />}
                    size="small"
                    style={{ width: 90, marginRight: 8 }}
                >
                    Rechercher
                </Button>
                <Button onClick={() => handleColumnReset(clearFilters)} size="small" style={{ width: 90 }}>
                    Réinitialiser
                </Button>
            </div>
        ),
        filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
        onFilter: (value, record) =>
            record[dataIndex]
                ?.toString()
                .toLowerCase()
                .includes(value.toLowerCase()),
    })

    /**
     * Gestion du changement de nombre de lignes
     */
    const handleNbRowChange = (value) => {
        Cookies.set('nbRow', value)
        setNbRow(value)
    }

    /**
     * Réinitialiser le formulaire
     */
    const handleReset = () => {
        setDateRange([
            moment().subtract(30, 'days'),
            moment()
        ])
        setData([])
        setRawData([])
        form.resetFields()
        message.info('Formulaire réinitialisé')
    }

    /**
     * Décode le contenu base64 et le retourne sous forme de texte
     */
    const decodeBase64Content = (content) => {
        try {
            console.log('🔄 Décodage base64, type:', typeof content, 'longueur:', content?.length)
            const decoded = atob(content)
            console.log('✅ Décodage réussi, longueur:', decoded.length)
            return decoded
        } catch (e) {
            console.error('❌ Erreur lors du décodage base64:', e)
            return null
        }
    }

    /**
     * Détecte le type MIME à partir de l'extension du fichier
     */
    const getMimeType = (filename) => {
        const extension = filename.split('.').pop().toLowerCase()
        const mimeTypes = {
            'csv': 'text/csv',
            'xls': 'application/vnd.ms-excel',
            'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'txt': 'text/plain'
        }
        return mimeTypes[extension] || 'application/octet-stream'
    }

    /**
     * Télécharge un fichier
     */
    const handleDownload = async (idJournalChFIn, direction, filename) => {
        const loadingKey = `${idJournalChFIn}-${direction}`
        setDownloadLoading(loadingKey)
        
        try {
            console.log('🔽 TÉLÉCHARGEMENT - Début', { idJournalChFIn, direction, filename })
            const response = await JournalChannelFileService.downloadFileContent(idJournalChFIn, direction)
            console.log('📦 TÉLÉCHARGEMENT - Response complète:', response)
            
            const base64Content = response.data
            console.log('📝 TÉLÉCHARGEMENT - Type du contenu:', typeof base64Content)
            console.log('📝 TÉLÉCHARGEMENT - Longueur du contenu:', base64Content ? base64Content.length : 0)
            console.log('📝 TÉLÉCHARGEMENT - Premiers 100 caractères:', base64Content ? base64Content.substring(0, 100) : 'vide')
            
            if (!base64Content) {
                message.error('Le fichier est vide ou non disponible')
                return
            }

            // Décode le base64 en données binaires
            console.log('🔄 TÉLÉCHARGEMENT - Décodage base64')
            const binaryString = atob(base64Content)
            console.log('🔓 TÉLÉCHARGEMENT - Binaire décodé, longueur:', binaryString.length)
            
            const bytes = new Uint8Array(binaryString.length)
            for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i)
            }
            console.log('✅ TÉLÉCHARGEMENT - Bytes créés:', bytes.length)

            // Crée un blob et télécharge
            const mimeType = getMimeType(filename)
            console.log('🎯 TÉLÉCHARGEMENT - MIME type:', mimeType)
            
            const blob = new Blob([bytes], { type: mimeType })
            console.log('📦 TÉLÉCHARGEMENT - Blob créé, taille:', blob.size)
            
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = filename.split('/').pop()
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)
            
            console.log('✅ TÉLÉCHARGEMENT - Terminé avec succès')
            message.success('Fichier téléchargé avec succès')
        } catch (error) {
            console.error('❌ TÉLÉCHARGEMENT - Erreur:', error)
            console.error('❌ TÉLÉCHARGEMENT - Stack:', error.stack)
            message.error('Erreur lors du téléchargement du fichier')
        } finally {
            setDownloadLoading(null)
        }
    }

    /**
     * Prévisualise le contenu d'un fichier
     */
    const handlePreview = async (idJournalChFIn, direction, filename) => {
        const loadingKey = `${idJournalChFIn}-${direction}`
        setDownloadLoading(loadingKey)
        
        try {
            console.log('👁️ PRÉVISUALISATION - Début', { idJournalChFIn, direction, filename })
            const response = await JournalChannelFileService.downloadFileContent(idJournalChFIn, direction)
            console.log('📦 PRÉVISUALISATION - Response complète:', response)
            
            const base64Content = response.data
            console.log('📝 PRÉVISUALISATION - Type du contenu:', typeof base64Content)
            console.log('📝 PRÉVISUALISATION - Longueur du contenu:', base64Content ? base64Content.length : 0)
            console.log('📝 PRÉVISUALISATION - Premiers 100 caractères:', base64Content ? base64Content.substring(0, 100) : 'vide')
            
            if (!base64Content) {
                message.error('Le fichier est vide ou non disponible')
                return
            }

            // Décode le contenu
            const decodedContent = decodeBase64Content(base64Content)
            console.log('🔓 PRÉVISUALISATION - Contenu décodé, longueur:', decodedContent ? decodedContent.length : 0)
            console.log('📝 PRÉVISUALISATION - Premiers 200 caractères décodés:', decodedContent ? decodedContent.substring(0, 200) : 'vide')
            
            if (!decodedContent) {
                message.error('Impossible de décoder le contenu du fichier')
                return
            }

            // Déterminer le type de fichier
            const extension = filename.split('.').pop().toLowerCase()
            let fileType = 'text'
            let parsedData = []

            if (extension === 'csv') {
                fileType = 'csv'
                setPreviewContent(decodedContent)
            } else if (extension === 'xls' || extension === 'xlsx') {
                fileType = 'excel'
                // Parser le fichier Excel
                try {
                    // Convertir le contenu décodé en array buffer
                    const binaryString = decodedContent
                    const bytes = new Uint8Array(binaryString.length)
                    for (let i = 0; i < binaryString.length; i++) {
                        bytes[i] = binaryString.charCodeAt(i)
                    }
                    
                    // Lire le fichier Excel
                    const workbook = XLSX.read(bytes, { type: 'array' })
                    const firstSheet = workbook.Sheets[workbook.SheetNames[0]]
                    let data = XLSX.utils.sheet_to_json(firstSheet, { header: 1 })
                    
                    console.log('📊 Excel parsé - dimensions:', data.length, 'lignes x', data[0]?.length, 'colonnes')
                    
                    let headerData = []
                    
                    // Détecter si le fichier est en format vertical (OUT) : plus de lignes que de colonnes
                    const isVertical = data.length > 2 && data[0]?.length <= 2
                    
                    if (isVertical && direction === 'OUT') {
                        console.log('🔄 Format vertical détecté pour fichier OUT avec template')
                        console.log('📊 Structure du fichier:', data.length, 'lignes au total')
                        
                        // Structure fixe du template :
                        // Lignes 0-3 (index 0-3) : Ignorees
                        // Ligne 4 (index 4) : Bandeau ligne 1 (ligne 5 en Excel)
                        // Ligne 5 (index 5) : IGNOREE (ligne 6 en Excel)
                        // Ligne 6 (index 6) : Bandeau ligne 2 (ligne 7 en Excel)
                        // Lignes 7-8 (index 7-8) : Ignorees (lignes 8-9 en Excel)
                        // Lignes 9+ (index 9+) : Tableau complet (lignes 10+ en Excel)
                        
                        const TABLE_START = 10    // Ligne 10 en Excel = index 9 (debut du tableau)
                        
                        if (data.length >= TABLE_START) {
                            // Extraire le bandeau : seulement lignes 5 et 7 (indices 4 et 6)
                            // On ignore la ligne 6 (index 5)
                            headerData = [
                                data[4],  // Ligne 5 en Excel
                                data[6]   // Ligne 7 en Excel
                            ].filter(row => row && row.length > 0)  // Filtrer les lignes vides
                            console.log(`Bandeau extrait: lignes 5 et 7 (${headerData.length} lignes)`)
                            
                            // Extraire le tableau : a partir de la ligne 10 en Excel (index 9)
                            data = data.slice(TABLE_START)
                            console.log(`Tableau extrait: a partir de la ligne ${TABLE_START + 1} (${data.length} lignes)`)
                        } else {
                            console.warn('Fichier trop court pour la structure attendue')
                        }
                        
                        // Si le tableau est toujours vertical, le transposer
                        if (data.length > 2 && data[0]?.length <= 2) {
                            console.log('🔄 Transposition du tableau')
                            const transposed = []
                            const maxCols = Math.max(...data.map(row => row.length))
                            for (let col = 0; col < maxCols; col++) {
                                const newRow = []
                                for (let row = 0; row < data.length; row++) {
                                    newRow.push(data[row][col] !== undefined ? data[row][col] : '')
                                }
                                transposed.push(newRow)
                            }
                            data = transposed
                            console.log('✅ Transposition effectuée - nouvelles dimensions:', data.length, 'lignes x', data[0]?.length, 'colonnes')
                        }
                    }
                    
                    parsedData = data
                    setPreviewHeader(headerData)
                    console.log('📊 Excel parsé avec succès:', data.length, 'lignes')
                } catch (error) {
                    console.error('❌ Erreur lors du parsing Excel:', error)
                    message.error('Erreur lors de la lecture du fichier Excel')
                    return
                }
            } else {
                setPreviewContent(decodedContent)
            }

            setPreviewFilename(filename.split('/').pop())
            setPreviewFileType(fileType)
            setPreviewData(parsedData)
            setPreviewDirection(direction)
            setPreviewVisible(true)
            console.log('✅ PRÉVISUALISATION - Modal ouverte avec succès, type:', fileType, 'direction:', direction)
        } catch (error) {
            console.error('❌ PRÉVISUALISATION - Erreur:', error)
            console.error('❌ PRÉVISUALISATION - Stack:', error.stack)
            message.error('Erreur lors de la prévisualisation du fichier')
        } finally {
            setDownloadLoading(null)
        }
    }

    /**
     * Ferme la modal de prévisualisation
     */
    const handleClosePreview = () => {
        setPreviewVisible(false)
        setPreviewContent('')
        setPreviewFilename('')
        setPreviewFileType('text')
        setPreviewData([])
        setPreviewHeader([])
        setPreviewDirection('IN')
    }

    /**
     * Parse le contenu CSV et retourne un tableau de données
     */
    const parseCSV = (csvContent) => {
        const lines = csvContent.split('\n').filter(line => line.trim())
        if (lines.length === 0) return []
        
        // Détecte le séparateur (virgule ou point-virgule)
        const firstLine = lines[0]
        const separator = firstLine.includes(';') ? ';' : ','
        
        return lines.map(line => {
            // Gestion des guillemets et échappements
            const values = []
            let current = ''
            let inQuotes = false
            
            for (let i = 0; i < line.length; i++) {
                const char = line[i]
                if (char === '"') {
                    inQuotes = !inQuotes
                } else if (char === separator && !inQuotes) {
                    values.push(current.trim())
                    current = ''
                } else {
                    current += char
                }
            }
            values.push(current.trim())
            return values
        })
    }

    /**
     * Rend le contenu CSV sous forme de tableau
     */
    const renderCSVTable = () => {
        const data = parseCSV(previewContent)
        if (data.length === 0) return <div>Aucune donnée à afficher</div>
        
        // Première ligne = en-têtes
        const headers = data[0]
        const rows = data.slice(1)
        
        return (
            <div>
                <div style={{ marginBottom: 8, color: '#666' }}>
                    {rows.length} ligne(s) de données
                </div>
                <Table
                    dataSource={rows.map((row, idx) => ({ key: idx, ...row }))}
                    columns={headers.map((header, idx) => ({
                        title: header,
                        dataIndex: idx,
                        key: idx,
                        ellipsis: true,
                        width: 150
                    }))}
                    scroll={{ x: 'max-content', y: 500 }}
                    size="small"
                    pagination={{
                        pageSize: 50,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '20', '50', '100', '200', '500'],
                        showTotal: (total) => `Total: ${total} lignes`
                    }}
                    bordered
                />
            </div>
        )
    }

    /**
     * Rend le bandeau/template du fichier OUT
     */
    const renderHeader = () => {
        if (previewHeader.length === 0) return null
        
        // La premiere ligne (ligne 5 Excel) = en-tetes de colonnes
        // La deuxieme ligne (ligne 7 Excel) = valeurs
        const headers = previewHeader[0] || []
        const values = previewHeader[1] || []
        
        // Creer les colonnes a partir de la premiere ligne du bandeau
        const headerColumns = headers.map((header, idx) => ({
            title: header || `Colonne ${idx + 1}`,
            dataIndex: `col${idx}`,
            key: `col${idx}`,
            ellipsis: false,
            align: 'left',
            render: (text) => (
                <span style={{
                    fontWeight: 'bold',
                    color: '#262626',
                    fontFamily: 'monospace',
                    fontSize: '13px'
                }}>
                    {text || ''}
                </span>
            )
        }))
        
        // Preparer les donnees (seulement la ligne 7)
        const headerDataSource = [{
            key: 0,
            ...values.reduce((acc, val, idx) => {
                acc[`col${idx}`] = val !== undefined && val !== null ? val.toString() : ''
                return acc
            }, {})
        }]
        
        return (
            <Card 
                size="small" 
                title={<span><FileTextOutlined /> En-tete du fichier / Template</span>}
                style={{ marginBottom: 16, backgroundColor: '#f0f5ff', borderColor: '#adc6ff' }}
            >
                <Table
                    dataSource={headerDataSource}
                    columns={headerColumns}
                    pagination={false}
                    size="small"
                    bordered
                    showHeader={true}
                    style={{ backgroundColor: '#fff' }}
                />
            </Card>
        )
    }

    /**
     * Rend le contenu Excel sous forme de tableau
     */
    const renderExcelTable = () => {
        if (previewData.length === 0) return <div>Aucune donnée à afficher</div>
        
        // Première ligne = en-têtes
        const headers = previewData[0] || []
        const rows = previewData.slice(1)
        
        return (
            <div>
                {/* Afficher le bandeau seulement pour les fichiers OUT */}
                {previewDirection === 'OUT' && renderHeader()}
                
                <div style={{ marginBottom: 8, color: '#666' }}>
                    <strong>Tableau de données:</strong> {rows.length} ligne(s)
                </div>
                <Table
                    dataSource={rows.map((row, idx) => {
                        const rowData = { key: idx }
                        row.forEach((cell, cellIdx) => {
                            rowData[cellIdx] = cell
                        })
                        return rowData
                    })}
                    columns={headers.map((header, idx) => ({
                        title: header || `Colonne ${idx + 1}`,
                        dataIndex: idx,
                        key: idx,
                        ellipsis: true,
                        width: 150,
                        render: (text) => text !== undefined && text !== null ? text.toString() : ''
                    }))}
                    scroll={{ x: 'max-content', y: previewDirection === 'OUT' ? 350 : 500 }}
                    size="small"
                    pagination={{
                        pageSize: 50,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '20', '50', '100', '200', '500'],
                        showTotal: (total) => `Total: ${total} lignes`
                    }}
                    bordered
                />
            </div>
        )
    }

    /**
     * Définition des colonnes du tableau
     */
    const columns = [
        {
            title: 'Fichier Entrant',
            dataIndex: 'filenameIn',
            key: 'filenameIn',
            width: 200,
            ellipsis: {
                showTitle: false,
            },
            ...getColumnSearchProps('filenameIn'),
            sorter: (a, b) => (a.filenameIn || '').localeCompare(b.filenameIn || ''),
            render: (filename) => (
                <Tooltip placement="topLeft" title={filename}>
                    <span>{filename}</span>
                </Tooltip>
            )
        },
        {
            title: 'Date Exec Entrant',
            dataIndex: 'execIn',
            key: 'execIn',
            width: 160,
            sorter: (a, b) => moment(a.execIn).unix() - moment(b.execIn).unix(),
            render: (date) => date ? moment(date).format('DD/MM/YYYY HH:mm:ss') : '-',
        },
        {
            title: 'Actions Entrant',
            key: 'actionsIn',
            width: 120,
            align: 'center',
            render: (text, record) => (
                <Space size="small">
                    <Tooltip title="Consulter">
                        <Button
                            type="link"
                            icon={<EyeOutlined />}
                            size="small"
                            onClick={() => handlePreview(record.idIn, 'IN', record.filenameIn)}
                            loading={downloadLoading === `${record.idIn}-IN`}
                        />
                    </Tooltip>
                    <Tooltip title="Télécharger">
                        <Button
                            type="link"
                            icon={<DownloadOutlined />}
                            size="small"
                            onClick={() => handleDownload(record.idIn, 'IN', record.filenameIn)}
                            loading={downloadLoading === `${record.idIn}-IN`}
                        />
                    </Tooltip>
                </Space>
            )
        },
        {
            title: 'Fichier Sortant',
            dataIndex: 'filenameOut',
            key: 'filenameOut',
            width: 200,
            ellipsis: {
                showTitle: false,
            },
            ...getColumnSearchProps('filenameOut'),
            sorter: (a, b) => (a.filenameOut || '').localeCompare(b.filenameOut || ''),
            render: (filename) => {
                if (!filename) return '-'
                const shortName = filename.substring(filename.lastIndexOf('/') + 1)
                return (
                    <Tooltip placement="topLeft" title={shortName}>
                        <span>{shortName}</span>
                    </Tooltip>
                )
            }
        },
        {
            title: 'Date Exec Sortant',
            dataIndex: 'execOut',
            key: 'execOut',
            width: 160,
            sorter: (a, b) => moment(a.execOut).unix() - moment(b.execOut).unix(),
            defaultSortOrder: 'descend',
            render: (date) => date ? (
                    moment(date).format('DD/MM/YYYY HH:mm:ss')
            ) : '-',
        },
        {
            title: 'Actions Sortant',
            key: 'actionsOut',
            width: 120,
            align: 'center',
            render: (text, record) => {
                if (!record.filenameOut) return '-'
                return (
                    <Space size="small">
                        <Tooltip title="Consulter">
                            <Button
                                type="link"
                                icon={<EyeOutlined />}
                                size="small"
                                onClick={() => handlePreview(record.idIn, 'OUT', record.filenameOut)}
                                loading={downloadLoading === `${record.idIn}-OUT`}
                            />
                        </Tooltip>
                        <Tooltip title="Télécharger">
                            <Button
                                type="link"
                                icon={<DownloadOutlined />}
                                size="small"
                                onClick={() => handleDownload(record.idIn, 'OUT', record.filenameOut)}
                                loading={downloadLoading === `${record.idIn}-OUT`}
                            />
                        </Tooltip>
                    </Space>
                )
            }
        }
    ]

    return (
        <>
            {hasActionByModule('Facture', 'Accéder') && (
                <div>
                    <Card style={{ marginBottom: 16 }}>
                        <Form
                            form={form}
                            layout="inline"
                            initialValues={{
                                dateRange: dateRange
                            }}
                        >
                            <Row gutter={16} style={{ width: '100%' }} align="middle">
                                <Col xs={24} sm={12} md={12}>
                                    <Form.Item
                                        label="Période"
                                        name="dateRange"
                                        rules={[{ required: true, message: 'Champ obligatoire' }]}
                                    >
                                        <RangePicker
                                            style={{ width: '100%' }}
                                            format="DD/MM/YYYY"
                                            value={dateRange}
                                            onChange={(dates) => setDateRange(dates)}
                                            placeholder={['Date début', 'Date fin']}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} md={12}>
                                    <Form.Item>
                                        <Space>
                                            <Button
                                                type="primary"
                                                icon={<SearchOutlined />}
                                                onClick={handleSearch}
                                                loading={loading}
                                            >
                                                Rechercher
                                            </Button>
                                            <Button
                                                icon={<ReloadOutlined />}
                                                onClick={handleReset}
                                            >
                                                Réinitialiser
                                            </Button>
                                        </Space>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Form>
                    </Card>

                    <Card>
                        <Row gutter={16} align="middle" justify="space-between" style={{ marginBottom: 16 }}>
                            <Col>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <div style={{ marginRight: 8 }}>Nombre de lignes:</div>
                                    <Select
                                        style={{ width: '80px' }}
                                        defaultValue={nbRow}
                                        onChange={handleNbRowChange}
                                    >
                                        <Option value="5">5</Option>
                                        <Option value="10">10</Option>
                                        <Option value="25">25</Option>
                                        <Option value="50">50</Option>
                                    </Select>
                                </div>
                            </Col>
                        </Row>
                        <Divider />
                        <Table
                            columns={columns}
                            dataSource={data}
                            loading={loading}
                            rowKey={(record) => `${record.idIn}-${record.idOut}`}
                            pagination={{ pageSize: parseInt(nbRow, 10) }}
                            scroll={{ x: 1200 }}
                            bordered
                        />
                    </Card>

                    {/* Modal de prévisualisation */}
                    <Modal
                        title={
                            <Space>
                                <FileTextOutlined />
                                <span>Prévisualisation: {previewFilename}</span>
                            </Space>
                        }
                        visible={previewVisible}
                        onCancel={handleClosePreview}
                        footer={[
                            <Button key="close" onClick={handleClosePreview}>
                                Fermer
                            </Button>
                        ]}
                        width={(previewFileType === 'csv' || previewFileType === 'excel') ? 1200 : 1000}
                        style={{ top: 20 }}
                    >
                        {previewFileType === 'csv' ? (
                            renderCSVTable()
                        ) : previewFileType === 'excel' ? (
                            renderExcelTable()
                        ) : (
                            <pre style={{
                                maxHeight: '500px',
                                overflow: 'auto',
                                backgroundColor: '#f5f5f5',
                                padding: '16px',
                                borderRadius: '4px',
                                fontSize: '12px',
                                lineHeight: '1.5',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word'
                            }}>
                                {previewContent}
                            </pre>
                        )}
                    </Modal>
                </div>
            )}
        </>
    )
}

JournalChannelFile.displayName = 'Historique'

export default JournalChannelFile
